import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { randToCents } from "@/lib/format";
import { toast } from "sonner";

/**
 * Shared editor for finance rate rules.
 *
 * Props:
 *   open, onClose, onSaved       — dialog control
 *   services                     — list of {id, label} service types
 *   editing                      — rate rule being edited, or null when creating
 *   customerAccountId (optional) — when set, new rates are created as
 *                                  customer-specific rates for this customer.
 *                                  When null/undefined, new rates are defaults.
 *   customerName      (optional) — display name for helper copy
 *
 * The legacy `kind` dropdown is intentionally NOT rendered in the UI. New
 * rates are always created with kind="flat". Existing rates preserve their
 * kind on PATCH (kind is not sent, so backend keeps the stored value).
 */
export const UNIT_OPTIONS = ["per_night", "per_wash", "per_load", "per_hour", "once_off", "unit"];

// Aligned with backend validation: lowercase letters, digits and underscores.
const CODE_HELPER = "Lowercase letters, digits, underscores and hyphens only (2–40 chars, e.g. parking_default).";

export default function RateEditor({
  open, onClose, onSaved, services, editing,
  customerAccountId, customerName,
}) {
  const isCustomerRate = !!customerAccountId;

  const [form, setForm] = useState({
    code: "", label: "", unit: "per_night",
    unit_price_rand: "", service_type_id: "", effective_from: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        code: editing.code,
        label: editing.label,
        unit: editing.unit || "per_night",
        unit_price_rand: (editing.unit_price_cents / 100).toString(),
        service_type_id: editing.service_type_id || "",
        effective_from: editing.effective_from || "",
      });
    } else {
      setForm({
        code: "", label: "", unit: "per_night",
        unit_price_rand: "", service_type_id: "", effective_from: "",
      });
    }
  }, [open, editing]);

  const bind = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.code || !form.label || !form.service_type_id) {
      toast.error("Code, label, and service are required");
      return;
    }
    setSaving(true);
    try {
      const basePayload = {
        label: form.label,
        unit: form.unit,
        unit_price_cents: randToCents(form.unit_price_rand),
        effective_from: form.effective_from || undefined,
      };
      if (editing) {
        await api.patch(`/finance/rate-rules/${editing.id}`, basePayload);
      } else {
        await api.post("/finance/rate-rules", {
          ...basePayload,
          code: form.code,
          service_type_id: form.service_type_id,
          kind: "flat",
          ...(isCustomerRate && { customer_account_id: customerAccountId }),
        });
      }
      toast.success(editing ? "Rate updated" : "Rate created");
      onSaved();
    } catch (e) {
      toast.error(formatError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const title = editing
    ? "Edit rate"
    : isCustomerRate
      ? `Add customer-specific rate${customerName ? ` — ${customerName}` : ""}`
      : "Add default rate";

  const description = editing
    ? "Historic invoices keep the price they were issued with."
    : isCustomerRate
      ? "This rate overrides the default service rate for this customer only."
      : "Default rates apply to every customer unless a customer-specific rate exists.";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="rate-editor-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-overline">Code</Label>
            <Input value={form.code} onChange={bind("code")}
                   data-testid="rate-editor-code"
                   disabled={!!editing}
                   placeholder="e.g. parking_default"
                   className="rounded h-10 mt-1 font-mono"/>
            <div className="text-[11px] text-slate-500 mt-1">{CODE_HELPER}</div>
          </div>
          <div>
            <Label className="label-overline">Label</Label>
            <Input value={form.label} onChange={bind("label")}
                   data-testid="rate-editor-label"
                   placeholder="e.g. Parking · default"
                   className="rounded h-10 mt-1"/>
          </div>
          <div>
            <Label className="label-overline">Service</Label>
            <select value={form.service_type_id} onChange={bind("service_type_id")}
                    data-testid="rate-editor-service" disabled={!!editing}
                    className="w-full mt-1 h-10 border border-slate-300 rounded px-2">
              <option value="">— Select service —</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Unit</Label>
              <select value={form.unit} onChange={bind("unit")}
                      data-testid="rate-editor-unit"
                      className="w-full mt-1 h-10 border border-slate-300 rounded px-2 font-mono">
                {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-overline">Price (R)</Label>
              <Input type="number" step="0.01" value={form.unit_price_rand}
                     onChange={bind("unit_price_rand")}
                     data-testid="rate-editor-price"
                     className="rounded h-10 mt-1 font-mono"/>
            </div>
          </div>
          <div>
            <Label className="label-overline">Effective from (optional)</Label>
            <Input type="date" value={form.effective_from} onChange={bind("effective_from")}
                   data-testid="rate-editor-effective"
                   className="rounded h-10 mt-1 font-mono"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded">Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  data-testid="rate-editor-save"
                  className="rounded bg-slate-900 hover:bg-slate-800">
            {saving ? "Saving…" : editing ? "Save" : "Create rate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
